import { Box, Button, Link, Stack, TextField } from '@customink/pigment-react';
import {
  usePricing,
  useProject,
  useQuotedVouchers,
} from '@customink/project-service-react-components';
import Collapse from '@mui/material/Collapse';
import type { ChangeEventHandler, FormEventHandler } from 'react';
import { useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';

import { useHasMultipleDesigns } from '@/hooks/Design';
import { useInternalPreview } from '@/hooks/internalPreview';
import { useUpdateVoucherCodeSync } from '@/hooks/Project';
import {
  PgmntColorInteractiveIconSubtle,
  colorRed,
  spacing12,
  spacing8,
} from '@/utils/designTokens';
import { fireEvent } from '@/utils/metrics';

export function VoucherInputLineItem() {
  const { data: project } = useProject();
  const voucherCode = project?.coreOrder?.voucherCode;
  const [voucherInput, setVoucherInput] = useState<string>(voucherCode || '');
  const [showVoucherField, setShowVoucherField] = useState<boolean>(false);
  const isInternalPreview = useInternalPreview();

  if (isInternalPreview) {
    return null;
  }

  const { isInvalid: isPricingInvalid, isLoading: isPricingLoading } =
    usePricing();

  const hasMultipleDesigns: boolean = useHasMultipleDesigns();
  const hasValidQuote = !isPricingInvalid && !isPricingLoading;

  // Save VoucherCode to Project
  const [updateVoucherCode, { isLoading: isUpdatingVoucherCode }] =
    useUpdateVoucherCodeSync();

  // Cast to our expected type
  const vouchersData = useQuotedVouchers();
  const voucherError = vouchersData?.error;
  const isVoucherApplicable = vouchersData?.hasApplicableVoucher;
  const voucherDoesNotSupportMultiDesign =
    vouchersData?.doesNotSupportMultiDesign;
  const voucherType = vouchersData?.vouchers?.[0]?.voucher_type;

  const showMultiItemError =
    hasMultipleDesigns && voucherDoesNotSupportMultiDesign;

  useEffect(() => {
    // When the voucher code has successfully been saved to the project,
    // and requoted with an applicable discount amount, hide the voucher field.
    if (hasValidQuote && voucherCode && !isVoucherApplicable) {
      setShowVoucherField(true);
    } else if (!voucherCode || isVoucherApplicable) {
      setShowVoucherField(false);
    }
  }, [hasValidQuote, isVoucherApplicable, voucherCode]);

  const onVoucherInputChange: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    setVoucherInput(event.target.value);
  };

  const onApplyClick: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const voucher = formData.get('voucherCode') as string;

    if (voucher.trim().length === 0) {
      setVoucherInput('');
      setShowVoucherField(false);
    }

    updateVoucherCode(voucher);
  };

  // Effect to handle voucher data updates
  useEffect(() => {
    if (
      !isUpdatingVoucherCode &&
      hasMultipleDesigns &&
      voucherDoesNotSupportMultiDesign
    ) {
      fireEvent('update_voucher', 'voucher_call_required', voucherType);
    }
  }, [
    hasMultipleDesigns,
    isUpdatingVoucherCode,
    voucherDoesNotSupportMultiDesign,
    voucherType,
  ]);

  const onLinkClick = () => {
    setShowVoucherField(
      (showVoucherFieldCurrentState) => !showVoucherFieldCurrentState,
    );
  };

  return (
    <Box
      data-testid="voucher-input-line-item"
      display="flex"
      flexDirection="column"
    >
      <Stack alignItems="center" direction="row" spacing="4px" useFlexGap>
        <Link
          data-testid="voucher-input-link"
          onClick={onLinkClick}
          variant="paragraphSmall"
          width="fit-content"
        >
          {!!voucherCode ? 'Edit discount code' : 'Discount code'}
        </Link>
        {!!isUpdatingVoucherCode && (
          <ClipLoader
            color={PgmntColorInteractiveIconSubtle}
            data-testid="voucher-input-loader"
            size={spacing12}
            speedMultiplier={0.65}
          />
        )}
      </Stack>

      <Collapse in={showVoucherField}>
        <form onSubmit={onApplyClick}>
          <Box display="flex" gap={spacing8} mt="0.5rem">
            <TextField
              aria-Label="voucher code"
              disabled={isUpdatingVoucherCode}
              error={voucherError != null || showMultiItemError}
              fullWidth
              helperText={
                showMultiItemError ? (
                  <div>
                    Sorry, discount codes can&apos;t be applied online for
                    multiple designs at this time. Please call us at{' '}
                    <a href="tel:+18002934232">800-293-4232</a> to apply your
                    discount.
                  </div>
                ) : (
                  voucherError
                )
              }
              inputProps={{ 'data-testid': 'voucher-text-field' }}
              name="voucherCode"
              onChange={onVoucherInputChange}
              placeholder="Enter discount code"
              value={voucherInput}
            />
            <Button
              data-testid="voucher-input-apply-button"
              disabled={isUpdatingVoucherCode}
              sx={{
                minWidth: 80,
              }}
              type="submit"
            >
              Apply
            </Button>
          </Box>
        </form>
      </Collapse>
    </Box>
  );
}
